import { useEffect, useState } from "react";
import { type useTranslate } from "@refinedev/core";

import { importTaak, useImportDialoogOpen, useImportTaak, verstreken } from "./importTaak";
import "./importTaak.css";

type Translate = ReturnType<typeof useTranslate>;

/**
 * De melding rechtsonder terwijl een document wordt nagebouwd, en als hij klaar is.
 *
 * <p>Hangt in de app, buiten elk scherm: je kunt tijdens het nabouwen naar een dier of een factuur,
 * en de melding reist mee. Staat het venster zelf open, dan toont dat al hoe het ervoor staat.
 */
export function ImportTaakMelding({ t, onBekijken }: { t: Translate; onBekijken: () => void }) {
  const taak = useImportTaak();
  const dialoogOpen = useImportDialoogOpen();
  const [nu, setNu] = useState(() => Date.now());

  useEffect(() => {
    if (taak.status !== "bezig") return;
    const id = setInterval(() => setNu(Date.now()), 1000);
    return () => clearInterval(id);
  }, [taak.status]);

  // Een herlaad gooit het resultaat weg: dan liever eerst de vraag van de browser.
  useEffect(() => {
    if (taak.status !== "bezig" && taak.status !== "klaar") return;
    const waarschuw = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", waarschuw);
    return () => window.removeEventListener("beforeunload", waarschuw);
  }, [taak.status]);

  if (taak.status === "leeg" || dialoogOpen) return null;

  return (
    <div
      className={`comm-importmelding comm-importmelding--${taak.status}`}
      role="status"
      aria-live="polite"
    >
      {taak.status === "bezig" ? (
        <span className="comm-import__spinner" aria-hidden="true" />
      ) : (
        <span className="comm-importmelding__icoon" aria-hidden="true">
          {taak.status === "klaar" ? "✓" : "!"}
        </span>
      )}
      <div className="comm-importmelding__tekst">
        <strong>
          {taak.status === "bezig" && t("communicatieImport.melding.bezig")}
          {taak.status === "klaar" && t("communicatieImport.melding.klaar")}
          {taak.status === "fout" && t("communicatieImport.melding.fout")}
          {taak.status === "bezig" && <span className="comm-import__teller">{verstreken(taak.gestart, nu)}</span>}
        </strong>
        <span>
          {t("communicatieImport.melding.voor", { args: [taak.bestandsnaam, taak.templateNaam] })}
        </span>
      </div>
      {taak.status !== "bezig" && (
        <button type="button" className="comm-importmelding__knop" onClick={onBekijken}>
          {t("communicatieImport.melding.bekijken")}
        </button>
      )}
      <button
        type="button"
        className="comm-importmelding__sluit"
        aria-label={t(taak.status === "bezig" ? "communicatieImport.stoppen" : "common.close")}
        title={t(taak.status === "bezig" ? "communicatieImport.stoppen" : "common.close")}
        onClick={() => importTaak.stop()}
      >
        ×
      </button>
    </div>
  );
}
